'use client';

import React, { useEffect, useState } from 'react';
import {
  Check,
  Download,
  Edit3,
  Eye,
  FileText,
  Film,
  Layers,
  Link2,
  MessageSquare,
  Mic,
  MicOff,
  Play,
  Sparkles,
  Type,
  Upload,
  Volume2,
  Zap
} from 'lucide-react';

// Script Writing Mockup - 5 script points
export const ScriptWritingMockup: React.FC = () => {
  const [lines, setLines] = useState<string[]>([]);
  const [currentLine, setCurrentLine] = useState(0);

  const scriptLines = [
    'Welcome to our product demo.',
    'Let me show you the dashboard.',
    'Click here to access settings.',
    "Now let's explore the features.",
    "That's how easy it is to use!"
  ];

  useEffect(() => {
    if (currentLine >= scriptLines.length) {
      const timeout = setTimeout(() => {
        setLines([]);
        setCurrentLine(0);
      }, 2000);
      return () => clearTimeout(timeout);
    }

    const timeout = setTimeout(() => {
      setLines((prev) => [...prev, scriptLines[currentLine]]);
      setCurrentLine((prev) => prev + 1);
    }, 800);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scriptLines is a constant array, only re-run on currentLine change
  }, [currentLine]);

  return (
    <div className="bg-muted/50 rounded-xl border border-border overflow-hidden h-full flex flex-col">
      <div className="bg-card border-b border-border px-3 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <span className="text-[11px] font-medium text-muted-foreground ml-2">
          AI Script Generator
        </span>
      </div>

      <div className="p-3 flex-1 flex flex-col">
        <div className="bg-card rounded-lg border border-border p-3 mb-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary animate-pulse" />
              <span className="text-[11px] font-semibold text-foreground">
                Generating Script
              </span>
            </div>
            <span className="text-[10px] font-bold text-primary">
              {lines.length}/{scriptLines.length}
            </span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all"
              style={{ width: `${(lines.length / scriptLines.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-3 flex-1 space-y-2">
          {lines.map((line, i) => (
            <div
              key={i}
              className="flex gap-2 animate-fade-in"
            >
              <span className="text-[10px] font-bold text-muted-foreground w-4">
                {i + 1}.
              </span>
              <span className="text-[11px] text-foreground leading-relaxed">
                {line}
              </span>
            </div>
          ))}
          {lines.length < scriptLines.length && (
            <div className="flex gap-2">
              <span className="text-[10px] font-bold text-muted-foreground w-4">
                {lines.length + 1}.
              </span>
              <span className="text-[11px] text-muted-foreground">
                Writing<span className="animate-pulse">...</span>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Voice Library Mockup - 4 voices
export const VoiceLibraryMockup: React.FC = () => {
  const [activeVoice, setActiveVoice] = useState(0);
  const [wavePhase, setWavePhase] = useState(0);

  const voices = [
    { name: 'Sarah', lang: 'US English', flag: '🇺🇸' },
    { name: 'James', lang: 'UK English', flag: '🇬🇧' },
    { name: 'Maria', lang: 'Spanish', flag: '🇪🇸' },
    { name: 'Hans', lang: 'German', flag: '🇩🇪' }
  ];

  useEffect(() => {
    const voiceInterval = setInterval(() => {
      setActiveVoice((prev) => (prev + 1) % voices.length);
    }, 1800);
    const waveInterval = setInterval(() => {
      setWavePhase((p) => p + 1);
    }, 80);
    return () => {
      clearInterval(voiceInterval);
      clearInterval(waveInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only animation with constant data
  }, []);

  return (
    <div className="bg-muted/50 rounded-xl border border-border overflow-hidden h-full flex flex-col">
      <div className="bg-card border-b border-border px-3 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <span className="text-[11px] font-medium text-muted-foreground ml-2">
          Voice Library
        </span>
      </div>

      <div className="p-3 flex-1 space-y-2">
        {voices.map((voice, i) => (
          <div
            key={i}
            className={`bg-card rounded-lg border p-2.5 transition-all ${
              activeVoice === i ? 'border-primary shadow-sm' : 'border-border'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="text-base">{voice.flag}</span>
                <div>
                  <p className="text-[12px] font-semibold text-foreground">
                    {voice.name}
                  </p>
                  <p className="text-[10px] font-medium text-muted-foreground">
                    {voice.lang}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-0.5 h-5">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <div
                      key={j}
                      className={`w-0.5 rounded-full transition-all ${activeVoice === i ? 'bg-primary' : 'bg-muted'}`}
                      style={{
                        height:
                          activeVoice === i
                            ? `${8 + Math.abs(Math.sin((wavePhase + j * 2) * 0.15)) * 12}px`
                            : '6px'
                      }}
                    />
                  ))}
                </div>
                {activeVoice === i && (
                  <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-primary-foreground" />
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Voice Cloning Mockup - Improved with waveform visualization
export const VoiceCloningMockup: React.FC = () => {
  const [progress, setProgress] = useState(0);
  const [wavePhase, setWavePhase] = useState(0);

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((p) => (p >= 100 ? 0 : p + 1.5));
    }, 50);
    const waveInterval = setInterval(() => {
      setWavePhase((p) => p + 1);
    }, 50);
    return () => {
      clearInterval(progressInterval);
      clearInterval(waveInterval);
    };
  }, []);

  return (
    <div className="bg-muted/50 rounded-xl border border-border overflow-hidden h-full flex flex-col">
      <div className="bg-card border-b border-border px-3 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <span className="text-[11px] font-medium text-muted-foreground ml-2">
          Voice Clone Studio
        </span>
      </div>

      <div className="p-3 flex-1 flex flex-col">
        {/* Voice Sample Card */}
        <div className="bg-card rounded-lg border border-border p-3 mb-3">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 bg-primary/10 rounded-full flex items-center justify-center">
              <Mic className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-[12px] font-semibold text-foreground">
                Your Voice Sample
              </p>
              <p className="text-[10px] font-medium text-muted-foreground">
                voice_recording.mp3
              </p>
            </div>
            <div className="text-[10px] font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-full">
              Cloning...
            </div>
          </div>

          {/* Waveform */}
          <div className="flex items-center justify-center gap-0.5 h-10 bg-muted/50 rounded-lg px-3 border border-border">
            {Array.from({ length: 30 }).map((_, i) => (
              <div
                key={i}
                className="w-1 bg-primary/60 rounded-full transition-all"
                style={{
                  height: `${20 + Math.sin((wavePhase + i * 2) * 0.12) * 60}%`
                }}
              />
            ))}
          </div>
        </div>

        {/* Progress Section */}
        <div className="bg-card rounded-lg border border-border p-3 flex-1 flex flex-col justify-between">
          <div>
            <div className="flex justify-between text-[11px] mb-2">
              <span className="font-medium text-muted-foreground">
                Creating AI voice clone
              </span>
              <span className="font-bold text-primary">
                {Math.floor(progress)}%
              </span>
            </div>
            <div className="h-2.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-linear-to-r from-primary to-emerald-400 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Status steps */}
          <div className="flex justify-between mt-3">
            {['Analyze', 'Process', 'Clone'].map((step, i) => (
              <div
                key={i}
                className="flex items-center gap-1.5"
              >
                <div
                  className={`w-3 h-3 rounded-full ${progress > i * 35 ? 'bg-primary' : 'bg-muted'}`}
                />
                <span
                  className={`text-[10px] font-medium ${progress > i * 35 ? 'text-primary' : 'text-muted-foreground'}`}
                >
                  {step}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Timeline Editor Mockup - Improved with more details
export const TimelineEditorMockup: React.FC = () => {
  const [playhead, setPlayhead] = useState(10);
  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setPlayhead((p) => (p >= 90 ? 10 : p + 0.3));
    }, 50);
    return () => clearInterval(interval);
  }, [isPlaying]);

  const tracks = [
    {
      name: 'Video',
      icon: Film,
      color: 'blue',
      clips: [
        { start: 2, width: 35, label: 'Intro' },
        { start: 40, width: 55, label: 'Content' }
      ]
    },
    {
      name: 'Audio',
      icon: Volume2,
      color: 'emerald',
      clips: [{ start: 5, width: 90, label: 'Voiceover' }]
    },
    {
      name: 'Text',
      icon: Type,
      color: 'amber',
      clips: [
        { start: 8, width: 20, label: 'Title' },
        { start: 35, width: 25, label: 'Subtitle' },
        { start: 65, width: 28, label: 'CTA' }
      ]
    }
  ];

  return (
    <div className="bg-muted/50 rounded-xl border border-border overflow-hidden h-full flex flex-col">
      <div className="bg-card border-b border-border px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
          </div>
          <span className="text-[11px] font-medium text-muted-foreground ml-2">
            Timeline Editor
          </span>
        </div>
        <div className="flex gap-1">
          {['Cut', 'Trim'].map((t) => (
            <span
              key={t}
              className="text-[9px] font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded border border-border"
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      <div className="p-3 flex-1 flex flex-col">
        {/* Playback Controls */}
        <div className="flex items-center gap-2 mb-3 bg-card rounded-lg border border-border p-2.5">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="w-7 h-7 bg-primary rounded-lg flex items-center justify-center hover:bg-primary/90 shadow-sm"
          >
            <Play
              className="w-3.5 h-3.5 text-primary-foreground"
              style={{ marginLeft: isPlaying ? 0 : 1 }}
            />
          </button>
          <div className="flex-1 h-1.5 bg-muted rounded-full relative">
            <div
              className="h-full bg-primary/40 rounded-full"
              style={{ width: `${playhead}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full shadow-sm"
              style={{ left: `${playhead}%` }}
            />
          </div>
          <span className="text-[10px] font-mono font-medium text-muted-foreground w-10">
            0:
            {Math.floor(playhead / 3)
              .toString()
              .padStart(2, '0')}
          </span>
        </div>

        {/* Tracks */}
        <div className="bg-card rounded-lg border border-border p-2.5 flex-1 space-y-2">
          {tracks.map((track, idx) => {
            const IconComponent = track.icon;
            return (
              <div
                key={idx}
                className="flex items-center gap-2"
              >
                <div className="flex items-center gap-1.5 w-14">
                  <IconComponent
                    className={`w-3.5 h-3.5 text-${track.color}-500`}
                  />
                  <span className="text-[10px] font-medium text-muted-foreground">
                    {track.name}
                  </span>
                </div>
                <div className="flex-1 h-7 bg-muted/50 rounded-lg border border-border relative overflow-hidden">
                  {track.clips.map((clip, i) => (
                    <div
                      key={i}
                      className={`absolute inset-y-0.5 bg-${track.color}-100 dark:bg-${track.color}-900/30 border border-${track.color}-200 dark:border-${track.color}-700 rounded flex items-center justify-center`}
                      style={{
                        left: `${clip.start}%`,
                        width: `${clip.width}%`
                      }}
                    >
                      <span
                        className={`text-[8px] font-medium text-${track.color}-600 dark:text-${track.color}-400 truncate px-1`}
                      >
                        {clip.label}
                      </span>
                    </div>
                  ))}
                  <div
                    className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                    style={{ left: `${playhead}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Auto-Sync Mockup - Improved with better visualization
export const AutoSyncMockup: React.FC = () => {
  const [phase, setPhase] = useState(0);
  const [synced, setSynced] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setPhase((p) => {
        if (p >= 100) {
          setSynced(true);
          setTimeout(() => {
            setSynced(false);
          }, 2000);
          return 0;
        }
        return p + 1.5;
      });
    }, 40);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-muted/50 rounded-xl border border-border overflow-hidden h-full flex flex-col">
      <div className="bg-card border-b border-border px-3 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <span className="text-[11px] font-medium text-muted-foreground ml-2">
          Auto-Sync Engine
        </span>
      </div>

      <div className="p-3 flex-1 flex flex-col">
        {/* Waveform Comparison */}
        <div className="bg-card rounded-lg border border-border p-3 mb-3">
          <div className="flex gap-3 items-center">
            {/* Video waveform */}
            <div className="flex-1">
              <p className="text-[10px] font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
                <Film className="w-3 h-3" /> Video Track
              </p>
              <div className="flex items-center justify-center gap-0.5 h-8 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div
                    key={i}
                    className="w-1 bg-blue-400 rounded-full transition-all"
                    style={{
                      height: `${30 + Math.sin((phase + i * 3) * 0.1) * 50}%`
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Sync indicator */}
            <div className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all shadow-sm ${
                  synced
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted border border-border'
                }`}
              >
                {synced ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Link2 className="w-4 h-4 text-muted-foreground" />
                )}
              </div>
            </div>

            {/* Audio waveform */}
            <div className="flex-1">
              <p className="text-[10px] font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
                <Volume2 className="w-3 h-3" /> Audio Track
              </p>
              <div className="flex items-center justify-center gap-0.5 h-8 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div
                    key={i}
                    className="w-1 bg-primary rounded-full transition-all"
                    style={{
                      height: `${30 + Math.sin((phase + i * 3) * 0.1) * 50}%`
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="bg-card rounded-lg border border-border p-3 flex-1 flex flex-col justify-between">
          <div>
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-1.5">
                <Zap
                  className={`w-4 h-4 ${synced ? 'text-primary' : 'text-muted-foreground animate-pulse'}`}
                />
                <span className="text-[11px] font-semibold text-foreground">
                  {synced ? 'Perfectly Synced!' : 'Synchronizing...'}
                </span>
              </div>
              <span className="text-[11px] font-bold text-primary">
                {synced ? '100' : Math.floor(phase)}%
              </span>
            </div>

            <div className="h-2.5 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${synced ? 'bg-primary' : 'bg-linear-to-r from-blue-400 to-primary'}`}
                style={{ width: synced ? '100%' : `${phase}%` }}
              />
            </div>
          </div>

          {/* Sync metrics */}
          <div className="flex justify-between mt-3 gap-2">
            {[
              { label: 'Accuracy', value: '99.8%' },
              { label: 'Offset', value: '0ms' },
              { label: 'Frames', value: '1080' }
            ].map((metric, i) => (
              <div
                key={i}
                className="flex-1 bg-muted/50 rounded-lg border border-border p-2 text-center"
              >
                <p className="text-[11px] font-bold text-foreground">
                  {metric.value}
                </p>
                <p className="text-[9px] font-medium text-muted-foreground">
                  {metric.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Captions Mockup - 5 captions
export const CaptionsMockup: React.FC = () => {
  const [activeCaption, setActiveCaption] = useState(0);
  const [charIndex, setCharIndex] = useState(0);

  const captions = [
    { time: '00:01', text: 'Welcome to our tutorial' },
    { time: '00:04', text: "Let's explore the features" },
    { time: '00:07', text: 'Click here to continue' },
    { time: '00:10', text: 'Now you can see results' },
    { time: '00:13', text: "That's all for today!" }
  ];

  useEffect(() => {
    const currentText = captions[activeCaption].text;
    if (charIndex < currentText.length) {
      const timeout = setTimeout(() => setCharIndex((c) => c + 1), 30);
      return () => clearTimeout(timeout);
    } else {
      const timeout = setTimeout(() => {
        setActiveCaption((a) => (a + 1) % captions.length);
        setCharIndex(0);
      }, 800);
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- captions is a constant array
  }, [charIndex, activeCaption]);

  return (
    <div className="bg-muted/50 rounded-xl border border-border overflow-hidden h-full flex flex-col">
      <div className="bg-card border-b border-border px-3 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <span className="text-[11px] font-medium text-muted-foreground ml-2">
          Caption Generator
        </span>
      </div>

      <div className="p-3 flex-1 flex flex-col">
        {/* Stats */}
        <div className="flex gap-2 mb-3">
          <div className="flex-1 bg-card rounded-lg border border-border p-2 text-center">
            <p className="text-[13px] font-bold text-primary">98%</p>
            <p className="text-[9px] font-medium text-muted-foreground">
              Accuracy
            </p>
          </div>
          <div className="flex-1 bg-card rounded-lg border border-border p-2 text-center">
            <p className="text-[13px] font-bold text-foreground">
              {captions.length}
            </p>
            <p className="text-[9px] font-medium text-muted-foreground">
              Captions
            </p>
          </div>
          <div className="flex-1 bg-card rounded-lg border border-border p-2 text-center">
            <p className="text-[13px] font-bold text-foreground">EN</p>
            <p className="text-[9px] font-medium text-muted-foreground">
              Language
            </p>
          </div>
        </div>

        {/* Captions list */}
        <div className="bg-card rounded-lg border border-border p-2.5 flex-1 space-y-1">
          {captions.map((caption, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 p-1.5 rounded-lg transition-all ${
                activeCaption === i ? 'bg-primary/10' : ''
              }`}
            >
              <span className="text-[9px] font-mono font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                {caption.time}
              </span>
              <span className="text-[11px] font-medium text-foreground flex-1 truncate">
                {activeCaption === i
                  ? caption.text.slice(0, charIndex) +
                    (charIndex < caption.text.length ? '|' : '')
                  : i < activeCaption
                    ? caption.text
                    : '...'}
              </span>
              {i <= activeCaption && (
                <Check className="w-3.5 h-3.5 text-primary shrink-0" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Export Mockup - Improved with buttons
export const ExportMockup: React.FC = () => {
  const [progress, setProgress] = useState(0);
  const [format, setFormat] = useState(0);

  const formats = [
    { name: 'MP4', quality: '4K', size: '120MB' },
    { name: 'WebM', quality: '720p', size: '28MB' },
    { name: 'MOV', quality: '4K', size: '120MB' }
  ];

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((p) => (p >= 100 ? 0 : p + 0.8));
    }, 50);
    const formatInterval = setInterval(() => {
      setFormat((f) => (f + 1) % formats.length);
    }, 3500);
    return () => {
      clearInterval(progressInterval);
      clearInterval(formatInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only animation with constant data
  }, []);

  return (
    <div className="bg-muted/50 rounded-xl border border-border overflow-hidden h-full flex flex-col">
      <div className="bg-card border-b border-border px-3 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <span className="text-[11px] font-medium text-muted-foreground ml-2">
          Export Studio
        </span>
      </div>

      <div className="p-3 flex-1 flex flex-col">
        {/* Format Selection */}
        <div className="flex gap-2 mb-3">
          {formats.map((f, i) => (
            <div
              key={i}
              className={`flex-1 bg-card rounded-lg border p-2 text-center transition-all cursor-pointer ${
                format === i ? 'border-primary shadow-sm' : 'border-border'
              }`}
            >
              <p
                className={`text-[12px] font-bold ${format === i ? 'text-primary' : 'text-foreground'}`}
              >
                {f.name}
              </p>
              <p className="text-[9px] font-medium text-muted-foreground">
                {f.quality}
              </p>
            </div>
          ))}
        </div>

        {/* Export Progress Card */}
        <div className="bg-card rounded-lg border border-border p-3 flex-1 flex flex-col">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Download className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-[12px] font-semibold text-foreground">
                tutorial_final.{formats[format].name.toLowerCase()}
              </p>
              <p className="text-[10px] font-medium text-muted-foreground">
                {formats[format].quality} • {formats[format].size}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold text-primary">
                {Math.floor(progress)}%
              </p>
            </div>
          </div>

          <div className="h-2.5 bg-muted rounded-full overflow-hidden mb-2">
            <div
              className="h-full bg-linear-to-r from-primary to-emerald-400 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Export stats */}
          <div className="flex justify-between text-[10px] font-medium text-muted-foreground mb-3">
            <span>Speed: 2.4x</span>
            <span>ETA: {Math.ceil((100 - progress) / 10)}s</span>
            <span>
              {Math.floor((progress * parseFloat(formats[format].size)) / 100)}{' '}
              / {formats[format].size}
            </span>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-auto">
            <button className="flex-1 bg-primary text-primary-foreground text-[11px] font-semibold py-2 px-3 rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-1.5">
              <Upload className="w-3.5 h-3.5" />
              Export Video
            </button>
            <button className="flex-1 bg-muted text-foreground text-[11px] font-semibold py-2 px-3 rounded-lg hover:bg-muted/80 transition-colors flex items-center justify-center gap-1.5 border border-border">
              <Download className="w-3.5 h-3.5" />
              Download Video
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Voice Grid Mockup - Grid of voice options with waveforms
export const VoiceGridMockup: React.FC = () => {
  const [activeVoice, setActiveVoice] = useState(0);
  const [wavePhase, setWavePhase] = useState(0);

  const voices = [
    { name: 'Emma', style: 'Warm', color: 'bg-rose-500' },
    { name: 'Alex', style: 'Professional', color: 'bg-blue-500' },
    { name: 'Sophie', style: 'Friendly', color: 'bg-purple-500' },
    { name: 'Marcus', style: 'Authoritative', color: 'bg-amber-500' },
    { name: 'Lily', style: 'Energetic', color: 'bg-emerald-500' },
    { name: 'James', style: 'Calm', color: 'bg-cyan-500' }
  ];

  useEffect(() => {
    const voiceInterval = setInterval(() => {
      setActiveVoice((prev) => (prev + 1) % voices.length);
    }, 1500);
    const waveInterval = setInterval(() => {
      setWavePhase((p) => p + 1);
    }, 60);
    return () => {
      clearInterval(voiceInterval);
      clearInterval(waveInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only animation with constant data
  }, []);

  return (
    <div className="bg-muted/50 rounded-xl border border-border overflow-hidden h-full flex flex-col">
      <div className="bg-card border-b border-border px-3 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <span className="text-[11px] font-medium text-muted-foreground ml-2">
          Voice Library
        </span>
        <span className="ml-auto text-[10px] font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
          100+ Voices
        </span>
      </div>

      <div className="p-3 flex-1">
        <div className="grid grid-cols-2 gap-2 h-full">
          {voices.map((voice, i) => (
            <div
              key={i}
              className={`bg-card rounded-lg border p-2.5 transition-all cursor-pointer ${
                activeVoice === i
                  ? 'border-primary shadow-md ring-1 ring-primary/20'
                  : 'border-border hover:border-muted-foreground'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div
                  className={`w-7 h-7 ${voice.color} rounded-full flex items-center justify-center`}
                >
                  <span className="text-white text-[10px] font-bold">
                    {voice.name[0]}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-semibold text-foreground truncate">
                    {voice.name}
                  </p>
                  <p className="text-[9px] font-medium text-muted-foreground">
                    {voice.style}
                  </p>
                </div>
                {activeVoice === i && (
                  <div className="w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                    <Check className="w-2.5 h-2.5 text-primary-foreground" />
                  </div>
                )}
              </div>

              {/* Waveform */}
              <div className="flex items-center justify-center gap-0.5 h-5">
                {Array.from({ length: 8 }).map((_, j) => (
                  <div
                    key={j}
                    className={`w-0.5 rounded-full transition-all ${activeVoice === i ? voice.color : 'bg-muted'}`}
                    style={{
                      height:
                        activeVoice === i
                          ? `${40 + Math.abs(Math.sin((wavePhase + j * 2) * 0.15)) * 60}%`
                          : '30%'
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Team Collaboration Mockup - Shows team members working together
export const CollaborationMockup: React.FC = () => {
  const [activeUser, setActiveUser] = useState(0);
  const [comments, setComments] = useState<number[]>([0]);

  const teamMembers = [
    {
      name: 'Sarah M.',
      role: 'Editor',
      color: 'bg-blue-500',
      status: 'Editing'
    },
    {
      name: 'John D.',
      role: 'Reviewer',
      color: 'bg-emerald-500',
      status: 'Viewing'
    },
    {
      name: 'Lisa K.',
      role: 'Admin',
      color: 'bg-purple-500',
      status: 'Approved'
    },
    { name: 'Mike R.', role: 'Sales', color: 'bg-orange-500', status: 'Online' }
  ];

  useEffect(() => {
    const userInterval = setInterval(() => {
      setActiveUser((prev) => (prev + 1) % teamMembers.length);
    }, 2000);

    const commentInterval = setInterval(() => {
      setComments((prev) => {
        if (prev.length >= 3) return [0];
        return [...prev, prev.length];
      });
    }, 1500);

    return () => {
      clearInterval(userInterval);
      clearInterval(commentInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only animation with constant data
  }, []);

  return (
    <div className="bg-muted/50 rounded-xl border border-border overflow-hidden h-full flex flex-col">
      <div className="bg-card border-b border-border px-3 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <span className="text-[11px] font-medium text-muted-foreground ml-2">
          Team Collaboration
        </span>
        <div className="ml-auto flex items-center gap-1">
          <div className="flex -space-x-1.5">
            {teamMembers.slice(0, 3).map((member, i) => (
              <div
                key={i}
                className={`w-5 h-5 ${member.color} rounded-full border-2 border-card flex items-center justify-center`}
              >
                <span className="text-white text-[8px] font-bold">
                  {member.name[0]}
                </span>
              </div>
            ))}
          </div>
          <span className="text-[9px] text-muted-foreground ml-1">
            +{teamMembers.length - 3}
          </span>
        </div>
      </div>

      <div className="p-3 flex-1 flex flex-col">
        {/* Team Members Grid */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          {teamMembers.map((member, i) => (
            <div
              key={i}
              className={`bg-card rounded-lg border p-2 transition-all ${
                activeUser === i ? 'border-primary shadow-sm' : 'border-border'
              }`}
            >
              <div className="flex items-center gap-2">
                <div
                  className={`w-7 h-7 ${member.color} rounded-full flex items-center justify-center relative`}
                >
                  <span className="text-white text-[10px] font-bold">
                    {member.name[0]}
                  </span>
                  {activeUser === i && (
                    <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 rounded-full border border-card" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold text-foreground truncate">
                    {member.name}
                  </p>
                  <p className="text-[8px] text-muted-foreground">
                    {member.role}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-1.5">
                <span
                  className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full ${
                    member.status === 'Editing'
                      ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                      : member.status === 'Approved'
                        ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                        : member.status === 'Viewing'
                          ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                          : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {member.status}
                </span>
                {activeUser === i && (
                  <Edit3 className="w-2.5 h-2.5 text-primary animate-pulse" />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Activity Feed */}
        <div className="bg-card rounded-lg border border-border p-2.5 flex-1">
          <div className="flex items-center gap-1.5 mb-2">
            <MessageSquare className="w-3 h-3 text-primary" />
            <span className="text-[10px] font-semibold text-foreground">
              Recent Activity
            </span>
          </div>
          <div className="space-y-1.5">
            {comments.map((_, i) => (
              <div
                key={i}
                className="flex items-start gap-2 animate-fade-in"
              >
                <div
                  className={`w-4 h-4 ${teamMembers[i % teamMembers.length].color} rounded-full flex items-center justify-center shrink-0`}
                >
                  <span className="text-white text-[7px] font-bold">
                    {teamMembers[i % teamMembers.length].name[0]}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[9px] text-foreground">
                    <span className="font-semibold">
                      {teamMembers[i % teamMembers.length].name}
                    </span>
                    <span className="text-muted-foreground">
                      {i === 0
                        ? ' edited the script'
                        : i === 1
                          ? ' added a comment'
                          : ' approved changes'}
                    </span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Translation/Localization Mockup - Shows language translation with flags
export const TranslationMockup: React.FC = () => {
  const [activeLanguage, setActiveLanguage] = useState(0);
  const [progress, setProgress] = useState(100);

  const languages = [
    { name: 'English', flag: '🇺🇸', code: 'EN', translated: true },
    { name: 'Spanish', flag: '🇪🇸', code: 'ES', translated: true },
    { name: 'French', flag: '🇫🇷', code: 'FR', translated: true },
    { name: 'German', flag: '🇩🇪', code: 'DE', translated: false },
    { name: 'Japanese', flag: '🇯🇵', code: 'JP', translated: false }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveLanguage((prev) => (prev + 1) % languages.length);
    }, 2000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only animation with constant data
  }, []);

  useEffect(() => {
    setProgress(0);
    const timeout = setTimeout(() => setProgress(100), 100);
    return () => clearTimeout(timeout);
  }, [activeLanguage]);

  return (
    <div className="bg-muted/50 rounded-xl border border-border overflow-hidden h-full flex flex-col">
      <div className="bg-card border-b border-border px-3 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <span className="text-[11px] font-medium text-muted-foreground ml-2">
          Translation Engine
        </span>
      </div>

      <div className="p-3 flex-1 flex flex-col">
        {/* Translation Progress */}
        <div className="bg-card rounded-lg border border-border p-3 mb-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-primary" />
              <span className="text-[11px] font-semibold text-foreground">
                Translating to {languages[activeLanguage].name}
              </span>
            </div>
            <span className="text-[10px] font-bold text-primary">
              {progress}%
            </span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-1800 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Language List */}
        <div className="bg-card rounded-lg border border-border p-2.5 flex-1 space-y-1.5">
          {languages.map((lang, i) => (
            <div
              key={i}
              className={`flex items-center justify-between p-2 rounded-lg transition-all ${
                activeLanguage === i
                  ? 'bg-primary/10 border border-primary'
                  : 'bg-muted/30 border border-transparent'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <span className="text-base">{lang.flag}</span>
                <div>
                  <p className="text-[11px] font-semibold text-foreground">
                    {lang.name}
                  </p>
                  <p className="text-[9px] font-medium text-muted-foreground">
                    {lang.code}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {(lang.translated || activeLanguage === i) && (
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center ${
                      activeLanguage === i ? 'bg-primary' : 'bg-muted'
                    }`}
                  >
                    <Check
                      className={`w-3 h-3 ${activeLanguage === i ? 'text-primary-foreground' : 'text-muted-foreground'}`}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div className="bg-card rounded-lg border border-border p-2 text-center">
            <p className="text-[14px] font-bold text-primary">65+</p>
            <p className="text-[9px] text-muted-foreground">Languages</p>
          </div>
          <div className="bg-card rounded-lg border border-border p-2 text-center">
            <p className="text-[14px] font-bold text-primary">~5s</p>
            <p className="text-[9px] text-muted-foreground">Per Video</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Visual Analysis Mockup - Shows AI analyzing video frames to generate script
export const VisualAnalysisMockup: React.FC = () => {
  const [currentFrame, setCurrentFrame] = useState(0);
  const [scriptLines, setScriptLines] = useState<string[]>([]);

  const frames = [
    { time: '0:03', action: 'Dashboard Overview', detected: true },
    { time: '0:08', action: 'Click Settings', detected: true },
    { time: '0:12', action: 'Navigate Menu', detected: false },
    { time: '0:18', action: 'Form Submission', detected: false }
  ];

  const scripts = [
    "Here's the main dashboard...",
    'Click on settings to configure...',
    'Navigate through the menu...',
    'Submit the form to save...'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFrame((prev) => {
        const next = (prev + 1) % frames.length;
        if (next === 0) {
          setScriptLines([]);
        }
        return next;
      });
    }, 1800);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only animation with constant data
  }, []);

  useEffect(() => {
    if (!scriptLines.includes(scripts[currentFrame])) {
      setScriptLines((prev) => [...prev.slice(-2), scripts[currentFrame]]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scripts is a constant array, scriptLines intentionally excluded to avoid loop
  }, [currentFrame]);

  return (
    <div className="bg-muted/50 rounded-xl border border-border overflow-hidden h-full flex flex-col">
      <div className="bg-card border-b border-border px-3 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <span className="text-[11px] font-medium text-muted-foreground ml-2">
          Visual-to-Script AI
        </span>
      </div>

      <div className="p-3 flex-1 flex flex-col gap-2">
        {/* Video Frame Analysis */}
        <div className="bg-card rounded-lg border border-border p-3">
          <div className="flex items-center gap-2 mb-2.5">
            <Eye className="w-4 h-4 text-primary animate-pulse" />
            <span className="text-[11px] font-semibold text-foreground">
              Analyzing Video Frames
            </span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {frames.map((frame, i) => (
              <div
                key={i}
                className={`aspect-video bg-muted rounded-md border-2 transition-all flex flex-col items-center justify-center ${
                  currentFrame === i
                    ? 'border-primary shadow-sm'
                    : i <= currentFrame
                      ? 'border-green-400/50'
                      : 'border-border'
                }`}
              >
                <Film
                  className={`w-3.5 h-3.5 ${currentFrame === i ? 'text-primary' : 'text-muted-foreground'}`}
                />
                <span className="text-[8px] text-muted-foreground mt-1">
                  {frame.time}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Detected Actions */}
        <div className="bg-card rounded-lg border border-border p-3 flex-1">
          <div className="flex items-center gap-2 mb-2.5">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-[11px] font-semibold text-foreground">
              Detected Actions
            </span>
          </div>
          <div className="space-y-2">
            {frames.slice(0, currentFrame + 1).map((frame, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 animate-fade-in"
              >
                <div className="w-5 h-5 bg-green-500/20 rounded-full flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-green-500" />
                </div>
                <span className="text-[11px] text-foreground flex-1">
                  {frame.action}
                </span>
                <span className="text-[9px] text-muted-foreground">
                  {frame.time}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Generated Script Preview */}
        <div className="bg-card rounded-lg border border-primary/30 p-3">
          <div className="flex items-center gap-2 mb-2.5">
            <FileText className="w-4 h-4 text-primary" />
            <span className="text-[11px] font-semibold text-foreground">
              Generated Script
            </span>
          </div>
          <div className="space-y-1.5">
            {scriptLines.map((line, i) => (
              <p
                key={i}
                className="text-[10px] text-foreground/80 animate-fade-in leading-relaxed"
              >
                "{line}"
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Script Editor Mockup - Shows full script customization with editing
export const ScriptEditorMockup: React.FC = () => {
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedLine, setSelectedLine] = useState<number | null>(null);

  const scriptLines = [
    { text: 'Welcome to our product walkthrough.', edited: false },
    { text: 'First, let me show you the dashboard.', edited: true },
    { text: 'Click here to access your settings.', edited: false },
    { text: "Now let's explore the key features.", edited: true }
  ];

  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setCursorPosition((prev) => (prev + 1) % 3);
    }, 500);

    const lineInterval = setInterval(() => {
      setSelectedLine((prev) => (prev === null ? 1 : prev === 1 ? 3 : null));
    }, 2500);

    return () => {
      clearInterval(cursorInterval);
      clearInterval(lineInterval);
    };
  }, []);

  return (
    <div className="bg-muted/50 rounded-xl border border-border overflow-hidden h-full flex flex-col">
      <div className="bg-card border-b border-border px-3 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <span className="text-[11px] font-medium text-muted-foreground ml-2">
          Script Editor
        </span>
      </div>

      <div className="p-3 flex-1 flex flex-col gap-2">
        {/* Toolbar */}
        <div className="bg-card rounded-lg border border-border p-2.5 flex items-center gap-3">
          <div className="flex items-center gap-1.5 border-r border-border pr-3">
            <div className="w-7 h-7 bg-muted rounded-md flex items-center justify-center hover:bg-primary/10 cursor-pointer transition-colors">
              <Type className="w-3.5 h-3.5 text-foreground" />
            </div>
            <div className="w-7 h-7 bg-muted rounded-md flex items-center justify-center hover:bg-primary/10 cursor-pointer transition-colors">
              <span className="text-[11px] font-bold text-foreground">B</span>
            </div>
            <div className="w-7 h-7 bg-muted rounded-md flex items-center justify-center hover:bg-primary/10 cursor-pointer transition-colors">
              <span className="text-[11px] italic text-foreground">I</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-primary/10 rounded-md flex items-center justify-center">
              <Edit3 className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="text-[10px] text-muted-foreground">Edit Mode</span>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-[10px] text-primary font-medium">
              AI Suggestions
            </span>
          </div>
        </div>

        {/* Script Lines */}
        <div className="bg-card rounded-lg border border-border p-3 flex-1 space-y-2">
          {scriptLines.map((line, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 p-2.5 rounded-lg transition-all ${
                selectedLine === i
                  ? 'bg-primary/10 border border-primary'
                  : 'bg-muted/30 border border-transparent'
              }`}
            >
              <span className="text-[11px] font-bold text-muted-foreground w-5 text-center">
                {i + 1}
              </span>
              <div className="flex-1">
                <span className="text-[11px] text-foreground leading-relaxed">
                  {line.text}
                  {selectedLine === i && cursorPosition < 2 && (
                    <span className="inline-block w-0.5 h-3.5 bg-primary animate-pulse ml-0.5" />
                  )}
                </span>
              </div>
              {line.edited && (
                <span className="text-[9px] px-2 py-0.5 bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 rounded-full shrink-0">
                  edited
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Status Bar */}
        <div className="flex items-center justify-between px-1">
          <span className="text-[10px] text-muted-foreground">
            4 lines • 156 words
          </span>
          <span className="text-[10px] text-green-500 flex items-center gap-1">
            <Check className="w-3.5 h-3.5" /> Saved
          </span>
        </div>
      </div>
    </div>
  );
};

// Collaborative Script Editing Mockup - Multiple users editing script together
export const ScriptCollaborationMockup: React.FC = () => {
  const [activeEditor, setActiveEditor] = useState(0);
  const [comments, setComments] = useState<number[]>([0]);

  const editors = [
    { name: 'Alex M.', color: 'bg-blue-500', cursor: 2 },
    { name: 'Sarah K.', color: 'bg-emerald-500', cursor: 0 },
    { name: 'Mike T.', color: 'bg-purple-500', cursor: 3 }
  ];

  const scriptLines = [
    { text: 'Welcome to our demo video.', highlight: 1 },
    { text: "Today we'll explore the dashboard.", highlight: null },
    { text: 'Click settings to configure options.', highlight: 0 },
    { text: "Let's finish with a quick summary.", highlight: 2 }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveEditor((prev) => (prev + 1) % editors.length);
    }, 2000);

    const commentInterval = setInterval(() => {
      setComments((prev) => (prev.length >= 2 ? [0] : [...prev, prev.length]));
    }, 2500);

    return () => {
      clearInterval(interval);
      clearInterval(commentInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only animation with constant data
  }, []);

  return (
    <div className="bg-muted/50 rounded-xl border border-border overflow-hidden h-full flex flex-col">
      <div className="bg-card border-b border-border px-3 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <span className="text-[11px] font-medium text-muted-foreground ml-2">
          Collaborative Editing
        </span>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex -space-x-1.5">
            {editors.map((editor, i) => (
              <div
                key={i}
                className={`w-6 h-6 ${editor.color} rounded-full border-2 border-card flex items-center justify-center ${
                  activeEditor === i
                    ? 'ring-2 ring-primary ring-offset-1 ring-offset-card'
                    : ''
                }`}
              >
                <span className="text-white text-[9px] font-bold">
                  {editor.name[0]}
                </span>
              </div>
            ))}
          </div>
          <span className="text-[10px] text-green-500 font-medium">Live</span>
        </div>
      </div>

      <div className="p-3 flex-1 flex flex-col gap-2">
        {/* Script with multiple cursors */}
        <div className="bg-card rounded-lg border border-border p-3 flex-1">
          <div className="flex items-center gap-2 mb-3 pb-2.5 border-b border-border">
            <FileText className="w-4 h-4 text-primary" />
            <span className="text-[11px] font-semibold text-foreground">
              Script Document
            </span>
            <span className="text-[9px] text-muted-foreground ml-auto">
              3 editors active
            </span>
          </div>
          <div className="space-y-2">
            {scriptLines.map((line, i) => (
              <div
                key={i}
                className={`flex items-center gap-2.5 p-2 rounded-md transition-all ${
                  line.highlight !== null
                    ? `border-l-2 ${editors[line.highlight].color.replace('bg-', 'border-')} bg-muted/30`
                    : ''
                }`}
              >
                <span className="text-[10px] font-bold text-muted-foreground w-4 text-center">
                  {i + 1}
                </span>
                <div className="flex-1 relative">
                  <span className="text-[11px] text-foreground leading-relaxed">
                    {line.text}
                  </span>
                  {line.highlight !== null && (
                    <div
                      className={`absolute -right-1 top-1/2 -translate-y-1/2 w-5 h-5 ${editors[line.highlight].color} rounded-full flex items-center justify-center`}
                    >
                      <span className="text-white text-[8px] font-bold">
                        {editors[line.highlight].name[0]}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Comments Section */}
        <div className="bg-card rounded-lg border border-border p-3">
          <div className="flex items-center gap-2 mb-2.5">
            <MessageSquare className="w-4 h-4 text-primary" />
            <span className="text-[11px] font-semibold text-foreground">
              Comments
            </span>
            <span className="text-[9px] bg-primary/20 text-primary px-2 py-0.5 rounded-full ml-auto">
              {comments.length}
            </span>
          </div>
          <div className="space-y-2">
            {comments.map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 animate-fade-in"
              >
                <div
                  className={`w-5 h-5 ${editors[i % editors.length].color} rounded-full flex items-center justify-center shrink-0`}
                >
                  <span className="text-white text-[8px] font-bold">
                    {editors[i % editors.length].name[0]}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-[10px] text-foreground">
                    <span className="font-semibold">
                      {editors[i % editors.length].name}
                    </span>
                    <span className="text-muted-foreground">
                      {i === 0
                        ? ": Let's refine this line"
                        : ': Good suggestion!'}
                    </span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Education Voice Mockup - Shows professional voice selection for educational content
export const EducationVoiceMockup: React.FC = () => {
  const [activeVoice, setActiveVoice] = useState(0);
  const [wavePhase, setWavePhase] = useState(0);

  const voices = [
    { name: 'Prof. Sarah', type: 'Academic', clarity: 98 },
    { name: 'Dr. James', type: 'Lecturer', clarity: 96 },
    { name: 'Ms. Elena', type: 'Instructor', clarity: 97 }
  ];

  useEffect(() => {
    const voiceInterval = setInterval(() => {
      setActiveVoice((prev) => (prev + 1) % voices.length);
    }, 2000);
    const waveInterval = setInterval(() => {
      setWavePhase((p) => p + 1);
    }, 80);
    return () => {
      clearInterval(voiceInterval);
      clearInterval(waveInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only animation with constant data
  }, []);

  return (
    <div className="bg-muted/50 rounded-xl border border-border overflow-hidden h-full flex flex-col">
      <div className="bg-card border-b border-border px-3 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <span className="text-[11px] font-medium text-muted-foreground ml-2">
          Professional Voices
        </span>
      </div>

      <div className="p-3 flex-1 flex flex-col gap-2">
        {/* Voice Quality Header */}
        <div className="bg-card rounded-lg border border-border p-3">
          <div className="flex items-center gap-2 mb-2">
            <Volume2 className="w-4 h-4 text-primary" />
            <span className="text-[11px] font-semibold text-foreground">
              Articulate Teacher Voices
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Clear pronunciation for educational content
          </p>
        </div>

        {/* Voice Cards */}
        <div className="flex-1 space-y-2">
          {voices.map((voice, i) => (
            <div
              key={i}
              className={`bg-card rounded-lg border p-3 transition-all ${
                activeVoice === i ? 'border-primary shadow-sm' : 'border-border'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2.5">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      activeVoice === i ? 'bg-primary' : 'bg-muted'
                    }`}
                  >
                    <Mic
                      className={`w-4 h-4 ${activeVoice === i ? 'text-primary-foreground' : 'text-muted-foreground'}`}
                    />
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-foreground">
                      {voice.name}
                    </p>
                    <p className="text-[9px] text-muted-foreground">
                      {voice.type}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-bold text-primary">
                    {voice.clarity}%
                  </p>
                  <p className="text-[8px] text-muted-foreground">Clarity</p>
                </div>
              </div>
              {/* Waveform */}
              <div className="flex items-center gap-0.5 h-4 justify-center">
                {Array.from({ length: 20 }).map((_, j) => (
                  <div
                    key={j}
                    className={`w-0.5 rounded-full transition-all ${activeVoice === i ? 'bg-primary' : 'bg-muted'}`}
                    style={{
                      height:
                        activeVoice === i
                          ? `${4 + Math.abs(Math.sin((wavePhase + j * 1.5) * 0.12)) * 12}px`
                          : '4px'
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Lesson Timeline Mockup - Shows lesson pacing and chapter controls
export const LessonTimelineMockup: React.FC = () => {
  const [currentChapter, setCurrentChapter] = useState(0);
  const [progress, setProgress] = useState(0);

  const chapters = [
    { title: 'Introduction', duration: '2:30', type: 'intro' },
    { title: 'Key Concepts', duration: '5:45', type: 'content' },
    { title: 'Practice Exercise', duration: '3:20', type: 'activity' },
    { title: 'Summary', duration: '1:50', type: 'summary' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setCurrentChapter((c) => (c + 1) % chapters.length);
          return 0;
        }
        return prev + 2;
      });
    }, 100);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only animation with constant data
  }, []);

  return (
    <div className="bg-muted/50 rounded-xl border border-border overflow-hidden h-full flex flex-col">
      <div className="bg-card border-b border-border px-3 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <span className="text-[11px] font-medium text-muted-foreground ml-2">
          Lesson Timeline
        </span>
      </div>

      <div className="p-3 flex-1 flex flex-col gap-2">
        {/* Current Lesson Info */}
        <div className="bg-card rounded-lg border border-border p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-semibold text-foreground">
              Lesson Progress
            </span>
            <span className="text-[10px] text-primary font-medium">
              Chapter {currentChapter + 1}/{chapters.length}
            </span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all"
              style={{ width: `${currentChapter * 25 + progress * 0.25}%` }}
            />
          </div>
        </div>

        {/* Chapter List */}
        <div className="bg-card rounded-lg border border-border p-3 flex-1">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-4 h-4 text-primary" />
            <span className="text-[11px] font-semibold text-foreground">
              Chapter Pacing
            </span>
          </div>
          <div className="space-y-2">
            {chapters.map((chapter, i) => (
              <div
                key={i}
                className={`flex items-center gap-2.5 p-2 rounded-lg transition-all ${
                  currentChapter === i
                    ? 'bg-primary/10 border border-primary'
                    : i < currentChapter
                      ? 'bg-muted/50'
                      : 'bg-muted/20'
                }`}
              >
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                    i < currentChapter
                      ? 'bg-green-500'
                      : currentChapter === i
                        ? 'bg-primary'
                        : 'bg-muted'
                  }`}
                >
                  {i < currentChapter ? (
                    <Check className="w-3.5 h-3.5 text-white" />
                  ) : (
                    <span className="text-[9px] font-bold text-white">
                      {i + 1}
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-medium text-foreground">
                    {chapter.title}
                  </p>
                  {currentChapter === i && (
                    <div className="h-1 bg-muted rounded-full mt-1 overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  )}
                </div>
                <span className="text-[9px] text-muted-foreground">
                  {chapter.duration}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// No Microphone Mockup - Shows silent recording → AI voice replacement concept
export const NoMicrophoneMockup: React.FC = () => {
  const [phase, setPhase] = useState(0);
  const [wavePhase, setWavePhase] = useState(0);

  useEffect(() => {
    const phaseTimer = setInterval(() => setPhase((p) => (p + 1) % 3), 2500);
    const waveTimer = setInterval(() => setWavePhase((p) => p + 1), 80);
    return () => {
      clearInterval(phaseTimer);
      clearInterval(waveTimer);
    };
  }, []);

  return (
    <div className="bg-muted/50 rounded-xl border border-border overflow-hidden h-full flex flex-col">
      <div className="bg-card border-b border-border px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
          </div>
          <span className="text-[11px] font-medium text-muted-foreground">
            Voiceover Studio
          </span>
        </div>
        <div className="flex items-center gap-1 bg-red-50 dark:bg-red-950/30 rounded-full px-2 py-0.5 border border-red-200 dark:border-red-800">
          <MicOff className="w-3 h-3 text-red-400" />
          <span className="text-[9px] text-red-500 font-semibold">OFF</span>
        </div>
      </div>

      <div className="p-3 flex-1 flex flex-col gap-2">
        {/* Traditional way - crossed out */}
        <div className="bg-card rounded-lg border border-border p-2.5 relative">
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="w-full h-px bg-red-300 rotate-[-4deg]" />
          </div>
          <div className="opacity-40">
            <div className="flex items-center gap-2 mb-1.5">
              <Mic className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-[10px] font-semibold text-muted-foreground">
                Traditional Recording
              </span>
            </div>
            <div className="flex items-center gap-1 text-[9px] text-muted-foreground">
              <span>🎙️ Microphone</span>
              <span className="mx-1">+</span>
              <span>🤫 Quiet room</span>
              <span className="mx-1">+</span>
              <span>🔁 Retakes</span>
            </div>
          </div>
        </div>

        {/* AI Voice - the better way */}
        <div
          className={`bg-card rounded-lg border-2 transition-all duration-500 p-2.5 flex-1 flex flex-col ${phase >= 1 ? 'border-primary/50 shadow-sm' : 'border-border'}`}
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="text-[10px] font-bold text-foreground">
              AI Voice Instead
            </span>
            <span className="text-[8px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-semibold ml-auto">
              No mic needed
            </span>
          </div>

          <div className="space-y-1.5 mb-2">
            {[
              { name: 'Sarah', accent: 'US English', active: phase === 0 },
              { name: 'James', accent: 'UK English', active: phase === 1 },
              { name: 'Maria', accent: 'Spanish', active: phase === 2 }
            ].map((voice, i) => (
              <div
                key={i}
                className={`flex items-center gap-2 p-1.5 rounded-md transition-all ${voice.active ? 'bg-primary/10 border border-primary/30' : 'bg-muted/30'}`}
              >
                <div
                  className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold ${voice.active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                >
                  {voice.name[0]}
                </div>
                <div className="flex-1">
                  <span
                    className={`text-[9px] font-medium ${voice.active ? 'text-foreground' : 'text-muted-foreground'}`}
                  >
                    {voice.name}
                  </span>
                  <span className="text-[8px] text-muted-foreground ml-1.5">
                    {voice.accent}
                  </span>
                </div>
                {voice.active && (
                  <div className="flex items-center gap-px">
                    {Array.from({ length: 12 }).map((_, j) => (
                      <div
                        key={j}
                        className="w-[1.5px] rounded-full bg-primary/60 transition-all"
                        style={{
                          height: `${3 + Math.abs(Math.sin((wavePhase + j) * 0.4)) * 8}px`
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-auto bg-primary/5 rounded-md p-1.5 flex items-center gap-2">
            <Check className="w-3 h-3 text-primary" />
            <span className="text-[9px] text-foreground font-medium">
              Studio-quality audio from just text
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Browser Recording Mockup - Shows no-download, instant browser recording
export const BrowserRecordingMockup: React.FC = () => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setStep((s) => (s + 1) % 4), 2000);
    return () => clearInterval(timer);
  }, []);

  const steps = [
    { label: 'Open browser', icon: '🌐', done: step >= 0 },
    { label: 'Visit videomule.com', icon: '🔗', done: step >= 1 },
    { label: 'Click Record', icon: '⏺️', done: step >= 2 },
    { label: 'Start creating!', icon: '✨', done: step >= 3 }
  ];

  return (
    <div className="bg-muted/50 rounded-xl border border-border overflow-hidden h-full flex flex-col">
      <div className="bg-card border-b border-border px-3 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <div className="flex-1 rounded-md h-5 flex items-center px-2">
          <span className="text-[11px] text-muted-foreground">
            🔒 videomule.com/recorder
          </span>
        </div>
      </div>

      <div className="p-3 flex-1 flex flex-col gap-2">
        {/* No download message */}
        <div className="bg-card rounded-lg border border-border p-2.5 text-center">
          <div className="flex items-center justify-center gap-2 mb-1.5">
            <div className="relative">
              <Download className="w-4 h-4 text-muted-foreground/40" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-5 h-[1.5px] bg-red-400 rotate-45" />
              </div>
            </div>
            <span className="text-[11px] font-bold text-foreground">
              No Download Required
            </span>
          </div>
          <span className="text-[10px] text-muted-foreground">
            Works 100% in your browser
          </span>
        </div>

        {/* Quick start steps */}
        <div className="bg-card rounded-lg border border-border p-2.5 flex-1">
          <span className="text-[11px] font-semibold text-foreground mb-2 block">
            Start in seconds:
          </span>
          <div className="space-y-2">
            {steps.map((s, i) => (
              <div
                key={i}
                className={`flex items-center gap-2 p-1.5 rounded-md transition-all duration-500 ${step === i ? 'bg-primary/10 border border-primary/30' : s.done ? 'bg-muted/30' : 'bg-transparent'}`}
              >
                <div
                  className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] transition-all ${s.done ? 'bg-primary/10' : 'bg-muted/50'}`}
                >
                  {s.done && i < step ? (
                    <Check className="w-3 h-3 text-primary" />
                  ) : (
                    <span>{s.icon}</span>
                  )}
                </div>
                <span
                  className={`text-[11px] transition-all ${step === i ? 'text-foreground font-semibold' : s.done ? 'text-foreground/70' : 'text-muted-foreground'}`}
                >
                  {s.label}
                </span>
                {step === i && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Browser badges */}
        <div className="bg-card rounded-lg border border-border p-2 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {['Chrome', 'Firefox', 'Safari', 'Edge'].map((b) => (
              <div
                key={b}
                className="flex items-center gap-1"
              >
                <Check className="w-2.5 h-2.5 text-primary" />
                <span className="text-[10px] text-muted-foreground font-medium">
                  {b}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
